/**
 * @file `<TerminalView/>` — the xterm.js wrapper that renders inside
 *       the dark pane and bridges keystrokes to/from the {@link Terminal}
 *       service.
 * @module @labcraft/lab/ui/components/terminal-view
 *
 * Mounts an `@xterm/xterm` instance with the Purple Haze theme,
 * loads the fit-addon, and wires three bridges:
 *
 *   xterm.onData  → terminal.write(utf8(data))     (keystrokes → pty)
 *   terminal.onData → xterm.write(bytes)           (pty bytes → display)
 *   xterm.onResize → terminal.resize(cols, rows)   (viewport → pty winsize)
 *
 * A `ResizeObserver` on the host div re-runs `fit.fit()` so the xterm
 * grid tracks the split-pane divider drag and window-resize events.
 *
 * Why the xterm class is imported as `XTerm`: the upstream class is
 * named `Terminal`, which would collide with our service. Aliasing
 * keeps the component's identifier ergonomic (`new XTerm(...)`)
 * without polluting the type namespace.
 */

import { useEffect, useRef, type JSX } from 'react'
import { Terminal as XTerm } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import '@xterm/xterm/css/xterm.css'
import styles from './terminal_view.module.css'

/**
 * Minimal driver shape `<TerminalView/>` needs from its data source.
 * The chapter view's `Terminal` service satisfies this; the landing's
 * in-memory `LandingDemoTerminal` does too. Decouples the xterm
 * wrapper from the WebSocket-backed service.
 */
export interface TerminalDriver {
  /** Subscribe to bytes flowing from the pty (server → client). */
  onData(handler: (bytes: Uint8Array) => void): () => void
  /** Write bytes to the pty (client → server). */
  write(bytes: Uint8Array): void
  /** Notify the pty of a new viewport size in cols/rows. */
  resize(cols: number, rows: number): void
  /**
   * Subscribe to connection-status changes. Optional — only the
   * WS-backed `Terminal` service implements it; the landing's
   * in-memory demo doesn't. When present, the view refires
   * `fit.fit()` on `'connected'` so the initial resize message
   * reaches the pty even though the WS wasn't OPEN at mount
   * time (diagnosed post-#318: `WsSubscription.send` silently
   * drops messages until `ws.readyState === OPEN`).
   */
  onStatus?(handler: (status: string) => void): () => void
}

export type TerminalViewProps = {
  terminal: TerminalDriver
  /**
   * Bumping this value clears the xterm display buffer (does not
   * reset the pty). Owned by `<TerminalPane/>` so the header's
   * Clear button can drive it.
   */
  clearKey?: number
}

export function TerminalView({
  terminal,
  clearKey,
}: TerminalViewProps): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const xtermRef = useRef<XTerm | null>(null)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const xterm = new XTerm({
      fontFamily:
        "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 13,
      lineHeight: 1.5,
      cursorBlink: true,
      cursorStyle: 'block',
      // Purple Haze dark theme. Selection uses the Vivid brand
      // color at ~40% alpha; ANSI green uses the design's
      // nix-shell teal accent.
      theme: {
        background: '#0F0D2A',
        foreground: '#c3bfe6',
        cursor: '#AFA9EC',
        cursorAccent: '#0F0D2A',
        selectionBackground: 'rgba(83, 74, 183, 0.4)',
        black: '#0B0920',
        red: '#d86474',
        green: '#6FE2BD',
        yellow: '#e6c889',
        blue: '#7F77DD',
        magenta: '#AFA9EC',
        cyan: '#9bdce0',
        white: '#c3bfe6',
        brightBlack: '#3a3475',
        brightRed: '#ec8896',
        brightGreen: '#8fe8c9',
        brightYellow: '#f1d8a3',
        brightBlue: '#AFA9EC',
        brightMagenta: '#CECBF6',
        brightCyan: '#bce5e8',
        brightWhite: '#EEEDFE',
      },
      allowProposedApi: true,
    })
    const fit = new FitAddon()
    xterm.loadAddon(fit)
    xterm.open(container)
    xtermRef.current = xterm

    // Dev-only xterm exposure for the @labcraft/test-mcp
    // `lab.terminal.*` tools (umbrella #718 Phase 3). Vite's
    // production build replaces `import.meta.env.DEV` with `false`,
    // so dead-code elimination strips this block from the
    // production lab bundle entirely — verified at PR review against
    // the `dist-lab` Nix derivation. Mechanism: attach the xterm
    // instance to the container element under a known property
    // name, plus a `data-lc-term` attribute the MCP tools select
    // on. Property-on-element keeps the global scope clean (no
    // `window.__lcTerm` ambient surface).
    if (import.meta.env.DEV) {
      ;(container as HTMLDivElement & { __lcTerm?: XTerm }).__lcTerm = xterm
      container.setAttribute('data-lc-term', 'mounted')
    }

    const encoder = new TextEncoder()

    // Keystrokes → pty.
    const onDataDispose = xterm.onData(data => {
      terminal.write(encoder.encode(data))
    })

    // pty → xterm.
    const offServiceData = terminal.onData(bytes => {
      xterm.write(bytes)
    })

    // Viewport → pty winsize.
    const onResizeDispose = xterm.onResize(({ cols, rows }) => {
      terminal.resize(cols, rows)
    })

    // Container size → fit.fit(), then trim to absorb FitAddon overshoot.
    //
    // FitAddon computes `rows = floor(content_height / cell_height)`
    // using a slightly-floored cell-height estimate; the renderer
    // then draws each row at the actual (un-floored) cell height,
    // so the rendered grid overshoots the container — measured live
    // at ~14 px (≈1.8 cells) horizontally on a 101-col viewport. One
    // cell of trim wasn't enough; the last column's glyph was still
    // getting clipped against `.pane`'s `overflow:hidden`. Trim 2
    // cols + 1 row gives reliable breathing room across viewports.
    const fitAndTrim = (): void => {
      try {
        fit.fit()
        if (xterm.cols > 2 && xterm.rows > 1) {
          xterm.resize(xterm.cols - 2, xterm.rows - 1)
        }
      } catch {
        // fit() throws if the container has zero dimensions; safe
        // to ignore — the next observer tick will retry.
      }
    }
    fitAndTrim()
    const ro = new ResizeObserver(fitAndTrim)
    ro.observe(container)

    // Webfont race: the mount-time fit above measures cell size with
    // the fallback font (JetBrains Mono is usually still loading), so
    // the grid lands on too many rows for the real metrics — the
    // bottom rows (the prompt) then render past the container, hidden
    // under the mentor dock. The ResizeObserver never refires because
    // the *container* size never changed, only the font did. Re-fit
    // once the font set settles; a no-op when the font was already
    // cached at mount.
    let fontRefitCancelled = false
    document.fonts?.ready
      ?.then(() => {
        if (!fontRefitCancelled) fitAndTrim()
      })
      .catch(() => {})

    // Re-fire the resize on WS open. The initial `fit.fit()` above
    // runs synchronously during mount, before `terminal.connect()`'s
    // WS reaches OPEN — so the first `terminal.resize(cols, rows)`
    // is silently dropped by `WsSubscription.send` (gated on
    // `readyState === OPEN`). Without this, the guest stays at
    // firecracker's default 80×24 forever (htop / vim etc. render
    // in the wrong viewport).
    //
    // Calling `fit.fit()` again here doesn't help: when the
    // container dimensions are unchanged, `fit.fit()` →
    // `xterm.resize(same, same)` is a no-op and `xterm.onResize`
    // doesn't fire, so no WS frame goes out. Drive `terminal.resize`
    // directly with the cached cols/rows that xterm already knows
    // from the initial fit — forcing the frame through the now-open
    // WS.
    //
    // Diagnosed live post-#318 (a first-pass `fit.fit()` here
    // didn't fix htop's 80×24 render). Optional — only the
    // WS-backed `Terminal` service implements `onStatus`; the
    // landing demo doesn't and doesn't need the refire.
    const offStatus =
      terminal.onStatus?.(status => {
        if (status !== 'connected') return
        try {
          // Re-fire the winsize now that the WS is OPEN (the mount-time
          // fit ran before connect, so its frame was dropped by
          // `WsSubscription.send`'s readyState gate).
          //
          // No form-feed wake-up here anymore: the hypervisor preserves the
          // serial ring across resume, so the attach-replay repaints the
          // pre-pause screen — correctly timed at guest-ready (handle-wire).
          // The prior `\x0c`-on-connect raced ~1.4s ahead of the real
          // snapshot-load and hit a not-yet-resumed guest, leaving a blank
          // terminal until the first keystroke.
          terminal.resize(xterm.cols, xterm.rows)
        } catch {
          /* defensive — never let a resize-on-connect crash xterm */
        }
      }) ?? (() => {})

    return () => {
      fontRefitCancelled = true
      ro.disconnect()
      offStatus()
      onDataDispose.dispose()
      onResizeDispose.dispose()
      offServiceData()
      xterm.dispose()
      xtermRef.current = null
    }
  }, [terminal])

  // Clear-buffer trigger from the header.
  useEffect(() => {
    if (clearKey === undefined || clearKey === 0) return
    xtermRef.current?.clear()
  }, [clearKey])

  return <div ref={containerRef} className={styles.view} />
}
